import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeCtx = createContext({ theme: "system", resolved: "light", setTheme: () => {} });

function readStored() {
  try {
    const v = localStorage.getItem("cms.theme");
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch (_e) {
    /* ignore */
  }
  return "system";
}

function systemPref() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToDom(mode) {
  const html = document.documentElement;
  if (mode === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
  html.style.colorScheme = mode;
}

export function ThemeProvider({ children, initialTheme }) {
  const [theme, setThemeState] = useState(initialTheme || readStored());
  const [resolved, setResolved] = useState(() =>
    (initialTheme || readStored()) === "system" ? systemPref() : (initialTheme || readStored())
  );

  useEffect(() => {
    if (initialTheme && initialTheme !== theme) setThemeState(initialTheme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTheme]);

  useEffect(() => {
    try {
      localStorage.setItem("cms.theme", theme);
    } catch (_e) {
      /* ignore */
    }
    const effective = theme === "system" ? systemPref() : theme;
    setResolved(effective);
    applyToDom(effective);

    if (theme === "system" && typeof window !== "undefined" && window.matchMedia) {
      const mql = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e) => {
        const mode = e.matches ? "dark" : "light";
        setResolved(mode);
        applyToDom(mode);
      };
      mql.addEventListener?.("change", handler);
      return () => mql.removeEventListener?.("change", handler);
    }
    return undefined;
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next === "light" || next === "dark" || next === "system") setThemeState(next);
  }, []);

  return (
    <ThemeCtx.Provider value={{ theme, resolved, setTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeCtx);
}
